/**
 * Relative Time Formatting Utility
 *
 * Provides functions to format timestamps as human-readable relative time strings.
 * Examples: "just now", "5 minutes ago", "2 hours ago", "yesterday"
 *
 * Usage:
 * ```ts
 * import { formatRelativeTime, getRelativeTimeString } from "@/lib/formatRelativeTime";
 *
 * const timestamp = new Date(Date.now() - 5 * 60 * 1000);
 * console.log(formatRelativeTime(timestamp)); // "5 minutes ago"
 * ```
 */

// ============================================================================
// Types
// ============================================================================

export interface RelativeTimeOptions {
  /**
   * The reference time to compare against.
   * Defaults to the current time (Date.now()).
   */
  relativeTo?: Date | number;

  /**
   * Whether to use abbreviated format.
   * Example: "5m ago" instead of "5 minutes ago"
   */
  abbreviated?: boolean;

  /**
   * Maximum age (in ms) before showing absolute date.
   * Default: 7 days (604800000ms)
   */
  maxAge?: number;

  /**
   * Whether to include "ago" suffix.
   * Default: true
   */
  includeSuffix?: boolean;
}

export interface RelativeTimeResult {
  /** The formatted relative time string */
  text: string;
  /** The time difference in milliseconds */
  diffMs: number;
  /** Whether the time is in the past */
  isPast: boolean;
  /** Whether the result was truncated to absolute date */
  isAbsolute: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

const DEFAULT_MAX_AGE = WEEK;

// Time unit thresholds and labels
const TIME_UNITS = [
  { threshold: YEAR, singular: "year", plural: "years", abbrev: "y" },
  { threshold: MONTH, singular: "month", plural: "months", abbrev: "mo" },
  { threshold: WEEK, singular: "week", plural: "weeks", abbrev: "w" },
  { threshold: DAY, singular: "day", plural: "days", abbrev: "d" },
  { threshold: HOUR, singular: "hour", plural: "hours", abbrev: "h" },
  { threshold: MINUTE, singular: "minute", plural: "minutes", abbrev: "m" },
  { threshold: SECOND, singular: "second", plural: "seconds", abbrev: "s" },
] as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert a date input to milliseconds since epoch.
 */
function toMs(date: Date | number | string): number {
  if (typeof date === "number") {
    return date;
  }
  if (typeof date === "string") {
    return new Date(date).getTime();
  }
  return date.getTime();
}

/**
 * Format an absolute date for display.
 */
function formatAbsoluteDate(date: Date): string {
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

// ============================================================================
// Main Functions
// ============================================================================

/**
 * Get detailed relative time information.
 *
 * @param timestamp - The timestamp to format
 * @param options - Formatting options
 * @returns Detailed relative time result
 */
export function getRelativeTimeDetails(
  timestamp: Date | number | string,
  options: RelativeTimeOptions = {}
): RelativeTimeResult {
  const {
    relativeTo = Date.now(),
    abbreviated = false,
    maxAge = DEFAULT_MAX_AGE,
    includeSuffix = true,
  } = options;

  const targetMs = toMs(timestamp);
  const referenceMs = toMs(relativeTo);
  const diffMs = referenceMs - targetMs;
  const absDiffMs = Math.abs(diffMs);
  const isPast = diffMs > 0;

  // Check if we should show absolute date
  if (absDiffMs > maxAge) {
    return {
      text: formatAbsoluteDate(new Date(targetMs)),
      diffMs,
      isPast,
      isAbsolute: true,
    };
  }

  // Handle "just now" case
  if (absDiffMs < 30 * SECOND) {
    return {
      text: "just now",
      diffMs,
      isPast,
      isAbsolute: false,
    };
  }

  // Find the appropriate time unit
  for (const unit of TIME_UNITS) {
    if (absDiffMs >= unit.threshold) {
      const value = Math.floor(absDiffMs / unit.threshold);
      const label = abbreviated
        ? unit.abbrev
        : value === 1
          ? unit.singular
          : unit.plural;

      let text: string;
      if (abbreviated) {
        text = `${value}${label}`;
        if (includeSuffix && isPast) {
          text += " ago";
        }
      } else {
        text = `${value} ${label}`;
        if (includeSuffix) {
          text += isPast ? " ago" : " from now";
        }
      }

      return {
        text,
        diffMs,
        isPast,
        isAbsolute: false,
      };
    }
  }

  // Fallback (should not reach here)
  return {
    text: "just now",
    diffMs,
    isPast,
    isAbsolute: false,
  };
}

/**
 * Format a timestamp as a relative time string.
 *
 * @param timestamp - The timestamp to format
 * @param options - Formatting options
 * @returns The formatted relative time string
 *
 * @example
 * ```ts
 * formatRelativeTime(Date.now() - 5 * 60 * 1000); // "5 minutes ago"
 * formatRelativeTime(Date.now() - 2 * 60 * 60 * 1000); // "2 hours ago"
 * formatRelativeTime(Date.now() - 60 * 1000, { abbreviated: true }); // "1m ago"
 * ```
 */
export function formatRelativeTime(
  timestamp: Date | number | string,
  options: RelativeTimeOptions = {}
): string {
  return getRelativeTimeDetails(timestamp, options).text;
}

/**
 * Get a short relative time string (always abbreviated).
 *
 * @param timestamp - The timestamp to format
 * @returns Abbreviated relative time string (e.g., "5m ago")
 */
export function formatRelativeTimeShort(
  timestamp: Date | number | string
): string {
  return formatRelativeTime(timestamp, { abbreviated: true });
}

/**
 * Format the "last updated" text for data freshness indicators.
 *
 * @param timestamp - When the data was last fetched
 * @returns Formatted string like "Last updated 5 minutes ago"
 */
export function formatLastUpdated(timestamp: Date | number | string | null | undefined): string {
  if (!timestamp) {
    return "Never updated";
  }

  const result = getRelativeTimeDetails(timestamp);

  if (result.isAbsolute) {
    return `Last updated on ${result.text}`;
  }

  if (result.text === "just now") {
    return "Updated just now";
  }

  return `Last updated ${result.text}`;
}

/**
 * Check if data is considered stale based on age.
 *
 * @param timestamp - When the data was last fetched
 * @param staleAfterMs - Threshold in ms after which data is stale (default: 5 minutes)
 * @returns Whether the data is stale
 */
export function isDataStale(
  timestamp: Date | number | string | null | undefined,
  staleAfterMs: number = 5 * MINUTE
): boolean {
  if (!timestamp) {
    return true;
  }

  const age = Date.now() - toMs(timestamp);
  return age > staleAfterMs;
}

// ============================================================================
// Exports
// ============================================================================

export default formatRelativeTime;
